import { Link } from 'react-router-dom'
import { ArrowLeft, ChevronDown, ChevronUp, HelpCircle, Search, CreditCard, Layers, ShieldCheck, LifeBuoy } from 'lucide-react'
import { useState, useMemo } from 'react'
import * as stylex from '@stylexjs/stylex'
import { styles } from '@/styles/FAQPage.stylex'

interface FAQItem {
    question: string
    answer: React.ReactNode
}

const faqData: FAQItem[] = [
    // Getting Started
    {
        question: "How do I get started with AiBrush?",
        answer: "Getting started is easy! Simply sign up for an account, add credits to your balance, and start creating. Choose from our wide range of AI models for images, videos, music, speech, and sound effects. Each model has different capabilities and credit costs, so you can find the perfect fit for your project and budget."
    },
    {
        question: "What is AiBrush and what can I create?",
        answer: "AiBrush is a comprehensive AI-powered creative platform that lets you generate images, videos, music, speech, and sound effects from text descriptions. Whether you're a content creator, designer, marketer, or just exploring AI creativity, you can create professional-quality content in minutes instead of hours."
    },
    {
        question: "Do I need any technical skills to use AiBrush?",
        answer: "Not at all! AiBrush is designed to be user-friendly for everyone. You just need to write clear descriptions of what you want to create. The better your description, the better the results. We provide examples and tips to help you get the best outcomes."
    },

    // Credits & Billing
    {
        question: "What are credits and how do they work?",
        answer: <>Credits are our virtual currency for AI generation. Each AI model has a specific credit cost based on its complexity and capabilities. For example, basic image generation costs 2.5-15 credits, video generation costs 20-500 credits, music generation costs 6-50 credits, and speech generation costs 0.1-1 credit. Credits are provided through monthly or yearly subscription plans. Check our <a href="https://aibrush.co/pricing" target="_blank" rel="noopener noreferrer">pricing page</a> for detailed subscription plans and features.</>
    },
    {
        question: "How do I get credits?",
        answer: <>We offer flexible subscription plans to fit your needs. Our Creator Plan starts at $9.99/month for 1,000 credits, Pro Plan at $29.99/month for 5,000 credits, and Max Plan at $99.99/month for 15,000 credits. All plans include yearly billing options with 20% savings. You can also start with our free plan that gives you 100 credits on sign-up. Visit our <a href="https://aibrush.co/pricing" target="_blank" rel="noopener noreferrer">pricing page</a> to compare all plans and features.</>
    },
    {
        question: "Can I get a refund for unused credits?",
        answer: "Credits are non-refundable, but they never expire. You can use them anytime, and they're stored securely in your account. Since credits come with subscription plans, you can cancel your subscription at any time if you no longer need the service."
    },

    // AI Models & Usage
    {
        question: "How do I choose the right AI model for my project?",
        answer: <>Each AI model has different strengths and specializations. Check our detailed <Link to='/ai-models'>AI models overview page</Link> for examples, features, and use cases. For images, consider factors like style, quality, and speed. For videos, think about motion quality and duration. Our model comparison tools help you make informed decisions.</>
    },
    {
        question: "Can I use AiBrush for commercial projects?",
        answer: "Yes! All content generated through AiBrush can be used for commercial purposes. You own the rights to everything you create, including images, videos, music, and audio. This makes AiBrush perfect for marketing campaigns, product designs, content creation, and business projects."
    },
    {
        question: "What file formats do you support?",
        answer: "We support all major file formats: Images (PNG, JPG, WebP), Videos (MP4, WebM), Audio (MP3, WAV), and more. You can download your creations in high quality and use them in any software or platform that supports these formats."
    },
    {
        question: "What video export capabilities do you offer?",
        answer: <>After creating your full movie in our studio, you can export videos of any length without time restrictions. We use GPU acceleration to speed up the export processing, making it much faster than traditional rendering. Whether you're exporting a short clip or a feature-length film, our optimized export system handles it efficiently. Export limits vary by plan - check our <a href="https://aibrush.co/pricing" target="_blank" rel="noopener noreferrer">pricing page</a> for details on export quotas per hour.</>
    },

    // Image Generation
    {
        question: "What types of images can I create?",
        answer: "You can create virtually any type of image: realistic photos, artistic illustrations, logos, product mockups, character designs, landscapes, abstract art, and much more. Our models excel at different styles, from photorealistic to artistic, so you can choose the perfect aesthetic for your project."
    },
    {
        question: "How do I get better image results?",
        answer: <>Better results come from better descriptions. Be specific about style, composition, lighting, mood, and details. Include artistic references, specify image dimensions, and mention any particular elements you want to emphasize. Check our <Link to='/images-models/google-imagen/'>Google Image</Link> and <Link to='/images-models/runway-image'>Runway Images</Link> pages for examples and best practices.</>
    },
    {
        question: "Can I edit or modify generated images?",
        answer: <>Yes! Many of our image models support image-to-image generation, allowing you to modify existing images or use them as references. Try <Link to='/images-models/google-imagen/'>Google Imagen</Link> for advanced image editing capabilities. You can also use the generated images in any image editing software for further customization.</>
    },

    // Video Generation
    {
        question: "What video lengths can I create?",
        answer: "Video generation capabilities vary by model. Each model has its own default length setting - some models default to 5 seconds, others to 8 seconds, 10 seconds, or different durations depending on the specific model."
    },
    {
        question: "Can I create videos from images?",
        answer: <>Absolutely! Many of our video models support image-to-video generation. Try <Link to='/videos-models/pika-video'>Pika Video</Link> or <Link to='/videos-models/wan-video'>Wan Video</Link> for excellent image-to-video capabilities. You can upload an image and animate it, or use it as a starting point for video creation. This is great for bringing static designs to life or creating animated versions of your artwork.</>
    },
    {
        question: "What video quality can I expect?",
        answer: "Our video models produce high-quality output suitable for professional use. Quality varies by model, with premium models offering higher resolution, better motion, and more realistic results. Check individual model pages for specific quality specifications."
    },

    // Music & Audio
    {
        question: "Can I create music with vocals?",
        answer: <>Yes! Our music models can generate both instrumental and vocal music. Try <Link to='/music-models/suno'>Suno Music</Link> for excellent vocal generation capabilities. You can specify the type of vocals (male/female), style, genre, and mood. The models understand musical terminology and can create everything from pop songs to classical compositions.</>
    },
    {
        question: "What genres of music can I create?",
        answer: "Our music models cover a wide range of genres: pop, rock, classical, jazz, electronic, folk, hip-hop, and many more. You can specify the genre, mood, tempo, and instrumentation to get exactly the style you're looking for."
    },
    {
        question: "Can I create sound effects?",
        answer: <>Absolutely! Our <Link to='/sound-effects-models/elevenlabs-sound-effects'>ElevenLabs Sound Effects</Link> model can generate cinematic sounds, ambient noise, impact sounds, nature sounds, and much more. Perfect for video projects, games, podcasts, or any audio production work.</>
    },

    // Account & Security
    {
        question: "Is my content private and secure?",
        answer: "Absolutely. We take privacy and security seriously. Your generated content is private to your account, and we don't use it for training or share it with third parties. All data is encrypted and stored securely."
    },

    // Support
    {
        question: "How can I get help if I have issues?",
        answer: <>We're here to help! Visit our <Link to='/support'>support page</Link> or reach our support team at support@aibrush.co. We typically respond within 24 hours. For faster help, include details about your issue, account information, and any relevant screenshots in your email.</>
    }
]

export default function FAQPage() {
    const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set())
    const [searchQuery, setSearchQuery] = useState('')

    const toggleItem = (index: number) => {
        const newExpanded = new Set(expandedItems)
        if (newExpanded.has(index)) {
            newExpanded.delete(index)
        } else {
            newExpanded.add(index)
        }
        setExpandedItems(newExpanded)
    }

    const faqIndexMap = useMemo(() => {
        const map = new Map<FAQItem, number>()
        faqData.forEach((item, index) => map.set(item, index))
        return map
    }, [])

    const filteredFaqData = useMemo(() => {
        if (!searchQuery.trim()) return faqData
        const query = searchQuery.toLowerCase()
        return faqData.filter(item =>
            item.question.toLowerCase().includes(query) ||
            (typeof item.answer === 'string' && item.answer.toLowerCase().includes(query))
        )
    }, [searchQuery])

    return (
        <div {...stylex.props(styles.faqPage)}>
            <Link to="/" {...stylex.props(styles.backLink)}>
                <ArrowLeft size={20} />
                Back to Home
            </Link>

            <section {...stylex.props(styles.heroSection)}>
                <div {...stylex.props(styles.heroContent)}>
                    <div {...stylex.props(styles.heroIcon)}>
                        <HelpCircle size={80} />
                    </div>
                    <h2 {...stylex.props(styles.heroTitle)}>Find Answers to Common Questions</h2>
                    <p {...stylex.props(styles.heroDescription)}>
                        Everything you need to know about AiBrush, from getting started to advanced features.
                        Can't find what you're looking for? Our support team is always ready to help!
                    </p>
                    <div {...stylex.props(styles.heroStats)}>
                        <div {...stylex.props(styles.stat)}><Layers size={18} /> 20+ AI Models</div>
                        <div {...stylex.props(styles.stat)}><CreditCard size={18} /> Flexible Credits</div>
                        <div {...stylex.props(styles.stat)}><ShieldCheck size={18} /> Secure & Private</div>
                    </div>
                </div>
            </section>

            <section {...stylex.props(styles.searchSection)}>
                <div {...stylex.props(styles.searchContainer)}>
                    <div {...stylex.props(styles.searchInputWrapper)}>
                        <Search size={20} {...stylex.props(styles.searchIcon)} />
                        <input
                            type="text"
                            placeholder="Search FAQ by question or answer..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            {...stylex.props(styles.searchInput)}
                        />
                    </div>
                    {searchQuery && (
                        <div {...stylex.props(styles.searchResults)}>
                            Found {filteredFaqData.length} results
                        </div>
                    )}
                </div>
            </section>

            <section {...stylex.props(styles.faqList)}>
                <div {...stylex.props(styles.faqContainer)}>
                    {filteredFaqData.map((item) => {
                        const originalIndex = faqIndexMap.get(item) ?? -1
                        const isExpanded = expandedItems.has(originalIndex)
                        return (
                            <div key={originalIndex} {...stylex.props(styles.faqItem)}>
                                <button
                                    {...stylex.props(styles.faqQuestion)}
                                    onClick={() => toggleItem(originalIndex)}
                                    aria-expanded={isExpanded}
                                >
                                    <span {...stylex.props(styles.faqQuestionText)}>{item.question}</span>
                                    <div {...stylex.props(styles.faqChevron)}>
                                        {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                                    </div>
                                </button>
                                {isExpanded && (
                                    <div {...stylex.props(styles.faqAnswer)}>
                                        <div {...stylex.props(styles.faqAnswerText)}>
                                            {item.answer}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
            </section>

            <section {...stylex.props(styles.supportCta)}>
                <div {...stylex.props(styles.heroContent)}>
                    <h3 {...stylex.props(styles.ctaTitle)}>Still Need Help?</h3>
                    <p {...stylex.props(styles.heroDescription)}>
                        Our support team is here to assist you with any questions or issues you may have.
                    </p>
                    <Link to="/support" {...stylex.props(styles.ctaButton)}>
                        <LifeBuoy size={20} />
                        Contact Support
                    </Link>
                </div>
            </section>
        </div>
    )
}